import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import { 
  generatePremiumDueIdempotencyKey, 
  generatePaymentReceivedIdempotencyKey, 
  generatePremiumEarningIdempotencyKey 
} from '../../utils/idempotencyUtils';
import type { 
  PremiumDueRequest, 
  PaymentReceivedRequest, 
  PremiumEarningRequest,
  InsuranceJournal 
} from '../../types/Insurance';

// Premium lifecycle mutations
export const useRecordPremiumDue = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PremiumDueRequest): Promise<InsuranceJournal> => {
      const idempotencyKey = generatePremiumDueIdempotencyKey(
        data.clientId,
        data.costCenterId,
        data.amount,
        data.dueDate
      );
      return apiClient.recordPremiumDue(data, idempotencyKey);
    },
    onSuccess: () => {
      // Invalidate both insurance and ledger caches
      queryClient.invalidateQueries({ queryKey: ['client-balance-summary'] });
      queryClient.invalidateQueries({ queryKey: ['client-receivables-aging'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-report'] });
      // Cross-invalidate ledger caches
      queryClient.invalidateQueries({ queryKey: ['ledger', 'journals'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'reports'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'dashboard'] });
    },
    onError: (error) => {
      console.error('Failed to record premium due:', error);
    }
  });
};

export const useRecordPaymentReceived = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PaymentReceivedRequest): Promise<InsuranceJournal> => {
      const idempotencyKey = generatePaymentReceivedIdempotencyKey(
        data.clientId,
        data.costCenterId,
        data.amount,
        data.externalPaymentId,
        data.provider
      );
      return apiClient.recordPaymentReceived(data, idempotencyKey);
    },
    onSuccess: () => {
      // Invalidate both insurance and ledger caches
      queryClient.invalidateQueries({ queryKey: ['client-balance-summary'] });
      queryClient.invalidateQueries({ queryKey: ['client-receivables-aging'] });
      queryClient.invalidateQueries({ queryKey: ['payment-batches'] });
      // Cross-invalidate ledger caches
      queryClient.invalidateQueries({ queryKey: ['ledger', 'journals'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'reports'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'dashboard'] });
    },
    onError: (error) => {
      console.error('Failed to record payment received:', error);
    }
  });
};

export const useRecordPremiumEarning = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PremiumEarningRequest): Promise<InsuranceJournal> => {
      const idempotencyKey = generatePremiumEarningIdempotencyKey(
        data.clientId,
        data.costCenterId,
        data.amount,
        data.earningDate
      );
      return apiClient.recordPremiumEarning(data, idempotencyKey);
    },
    onSuccess: () => {
      // Invalidate both insurance and ledger caches
      queryClient.invalidateQueries({ queryKey: ['client-balance-summary'] });
      queryClient.invalidateQueries({ queryKey: ['premium-earning-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-report'] });
      // Cross-invalidate ledger caches
      queryClient.invalidateQueries({ queryKey: ['ledger', 'journals'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'reports'] });
      queryClient.invalidateQueries({ queryKey: ['ledger', 'dashboard'] });
    },
    onError: (error) => {
      console.error('Failed to record premium earning:', error);
    }
  });
};
